import { Box, CircularProgress, Container, Typography } from '@mui/material';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Navigate, useLocation } from 'react-router-dom';
import {
    AccessRefusedError,
    BackendUnavailableError,
} from '~/backend/backend-client';
import WorkspaceLogo from '~/components/Logo/WorkspaceLogo';
import { UnexpectedErrorPage } from '~/containers/errors/RouteErrorBoundary';
import { checkIsInSessionRouteWithoutRouter } from '~/hooks/session/useIsInSessionRoute';
import { isAuthenticationEnabled } from '~/lib/auth';
import {
    ACCESS_CHECK_RETRIES,
    useAccessRights,
} from '~/hooks/workflows/backend/useBackend';
import type {
    AccessDeniedReason,
    AccessDeniedState,
} from '~/containers/errors/AccessDeniedPage';
import AppConfig from '~/utils/appconfig';

/**
 * ProtectedRoute component that handles access rights validation.
 *
 * This component assumes the user is already authenticated (handled by AuthenticationGuard)
 * and focuses only on access rights validation.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
    const location = useLocation();
    const isInConnectorRoute = checkIsInSessionRouteWithoutRouter();

    // `authority` is the Entra authority alone, so testing it treated every
    // broker-only deployment as unauthenticated and skipped the licence check
    // entirely. isAuthenticationEnabled covers both issuers, which is the actual
    // question: is anyone required to sign in at all?
    const shouldSkipAccessCheck =
        AppConfig.accessRightKey === 'development' ||
        isInConnectorRoute ||
        !isAuthenticationEnabled();

    const {
        data: hasAccess,
        isLoading,
        error,
        failureCount,
        failureReason,
    } = useAccessRights(!shouldSkipAccessCheck);

    if (shouldSkipAccessCheck) {
        return <>{children}</>;
    }

    if (isLoading) {
        return (
            <Container maxWidth="sm">
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="100vh"
                    textAlign="center"
                    gap={4}
                >
                    <WorkspaceLogo height={120} />
                    <CircularProgress size={40} />
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        <FormattedMessage
                            id="checkingAccessRights"
                            defaultMessage="Checking access rights..."
                        />
                    </Typography>
                    {failureCount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            {failureReason instanceof
                            BackendUnavailableError ? (
                                <FormattedMessage
                                    id="accessCheck.unreachable"
                                    defaultMessage="Cannot reach the Backend API at {url}. Retrying (attempt {attempt} of {total})…"
                                    values={{
                                        url: AppConfig.backendUrl,
                                        attempt: failureCount + 1,
                                        total: ACCESS_CHECK_RETRIES + 1,
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id="accessCheck.retrying"
                                    defaultMessage="The access check got no answer. Retrying (attempt {attempt} of {total})…"
                                    values={{
                                        attempt: failureCount + 1,
                                        total: ACCESS_CHECK_RETRIES + 1,
                                    }}
                                />
                            )}
                        </Typography>
                    )}

                    <Typography
                        variant="caption"
                        display="block"
                        sx={{ mt: 4, opacity: 0.7 }}
                    >
                        <FormattedMessage
                            id="versionInfo"
                            defaultMessage="Version {version}"
                            values={{ version: AppConfig.version }}
                        />
                    </Typography>
                </Box>
            </Container>
        );
    }

    // A check that passed stays passed: a failed background refetch keeps
    // the cached answer, and must not unmount a viewer in use.
    if (hasAccess !== true) {
        // An outage says nothing about the account, so it gets the service
        // error page with diagnostics. Rendered in place: reloading asks again.
        if (error instanceof BackendUnavailableError) {
            return (
                <UnexpectedErrorPage
                    error={error}
                    boundary="access-check"
                    status={503}
                    title="Workspace+ Is Temporarily Unavailable"
                    description="The Workspace+ service could not be reached. This is not a problem with your account. Try again in a few minutes."
                />
            );
        }
        // Only an answer about the account names the account: a refusal, or
        // an explicit "no access". Anything else — a failure, or a check that
        // never ran because it was paused offline — is no answer at all.
        const reason: AccessDeniedReason =
            error instanceof AccessRefusedError
                ? 'refused'
                : hasAccess === false
                  ? 'unlicensed'
                  : 'unavailable';
        const state: AccessDeniedState = { reason, from: location };
        return <Navigate to="/access-denied" state={state} replace />;
    }

    return <>{children}</>;
}

export default ProtectedRoute;
